// @section 1 Mission Control route — six operational telemetry planes.
// Reads the /api/hq/ops/* aggregate endpoints (jobs / sessions / drift / github
// / deploys / campaigns). Each panel loads independently so one unwired
// datasource (503) never blanks the page. This page renders; it does not derive.
// The Jobs panel carries the single act-back: POST /jobs/ack (annotation only).

import { useCallback, useEffect, useState } from 'react';
import {
  getOpsJobs, ackOpsJob, getOpsSessions, getOpsDrift, getOpsGithub, getOpsDeploys, getOpsCampaigns,
  type OpsJobsPayload, type OpsSessionsPayload, type OpsDriftPayload,
  type OpsGithubPayload, type OpsDeploysPayload, type OpsCampaignsPayload,
} from '../api';

// @subsection 1.1 Display helpers

function usd(v: number | null | undefined): string {
  return v == null ? 'n/a' : `$${Math.round(v).toLocaleString()}`;
}
function num(v: number | null | undefined): string {
  return v == null ? 'n/a' : v.toLocaleString();
}
function pct(v: number | null | undefined): string {
  return v == null ? 'n/a' : `${Math.round(v * 100)}%`;
}
/** Relative "3h ago" / "2d ago" for a timestamp, or "never". */
function ago(iso: string | null | undefined): string {
  if (!iso) return 'never';
  const ms = Date.now() - new Date(iso).getTime();
  if (ms < 0) return 'just now';
  const m = Math.floor(ms / 60000);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

// verdict / severity → dot color (uses --mt-* tokens with hex fallbacks).
const VERDICT_COLOR: Record<string, string> = {
  OK: 'var(--mt-ok, #4ade80)',
  NEVER_RAN: '#6b7280',
  STALE: 'var(--mt-watch, #fbbf24)',
  FAILING: 'var(--mt-stall, #f87171)',
};
const SEVERITY_COLOR: Record<string, string> = {
  high: 'var(--mt-stall, #f87171)',
  medium: 'var(--mt-watch, #fbbf24)',
  low: '#6b7280',
};

// @subsection 1.2 Generic panel chrome

function Panel({ title, subtitle, error, loading, children }: {
  title: string; subtitle?: string; error?: string | null; loading?: boolean; children?: React.ReactNode;
}) {
  return (
    <div className="rounded-xl border border-paper-100/10 bg-ink-900 p-5">
      <div className="flex items-baseline justify-between mb-4">
        <div className="text-[11px] uppercase tracking-[2px] text-muted">{title}</div>
        {subtitle && <div className="text-[10px] text-muted">{subtitle}</div>}
      </div>
      {loading && <div className="text-sm text-muted">Loading…</div>}
      {error && (
        <div className="text-sm text-stall">
          {error.includes('503') ? 'Connection not configured.' : error}
        </div>
      )}
      {!loading && !error && children}
    </div>
  );
}

/** Big-number stat, Adam's big-simple-fonts preference. */
function Stat({ label, value, tone }: { label: string; value: string; tone?: string }) {
  return (
    <div>
      <div className="text-3xl md:text-4xl font-bold text-paper-100 tabular-nums" style={tone ? { color: tone } : undefined}>{value}</div>
      <div className="text-[11px] uppercase tracking-[1px] text-muted mt-1">{label}</div>
    </div>
  );
}

/** Standard load-on-mount hook returning [data, error, loading, reload]. */
function usePanel<T>(loader: () => Promise<T>): [T | null, string | null, boolean, () => void] {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const reload = useCallback(() => {
    setLoading(true); setError(null);
    loader().then((d) => { setData(d); setLoading(false); })
      .catch((e) => { setError(String(e)); setLoading(false); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(reload, [reload]);
  return [data, error, loading, reload];
}

// @section 2 Panels

// @block 2.1 Jobs — daily job health + absences + the act-back.
function JobsPanel() {
  const [data, error, loading, reload] = usePanel<OpsJobsPayload>(getOpsJobs);
  const [busy, setBusy] = useState<string | null>(null);

  const onAck = async (jobKey: string) => {
    setBusy(jobKey);
    try { await ackOpsJob(jobKey); reload(); }
    finally { setBusy(null); }
  };

  return (
    <Panel title="Daily jobs" subtitle={data ? `${data.jobs.length} tracked` : undefined}
           error={error} loading={loading}>
      {data && (
        <div className="space-y-1.5">
          {data.jobs.map((j) => (
            <div key={j.jobKey} className="flex items-center gap-3 py-1.5 border-t border-paper-100/5 first:border-t-0">
              <span className="inline-block w-2.5 h-2.5 rounded-full shrink-0"
                    style={{ background: VERDICT_COLOR[j.verdict ?? ''] ?? '#6b7280' }}
                    title={j.verdict ?? ''} />
              <div className="min-w-0 flex-1">
                <div className="text-sm text-paper-100/90 truncate">{j.displayName}</div>
                <div className="text-[11px] text-muted truncate">
                  {j.verdict ?? '—'} · {ago(j.lastRunAt)}
                  {j.error ? ` · ${j.error}` : ''}
                </div>
              </div>
              {j.latestAck ? (
                <span className="text-[10px] text-muted shrink-0" title={`by ${j.latestAck.acknowledgedBy ?? 'someone'} ${ago(j.latestAck.acknowledgedAt)}`}>
                  ✓ ack
                </span>
              ) : (
                <button
                  onClick={() => onAck(j.jobKey)}
                  disabled={busy === j.jobKey}
                  className="text-[10px] uppercase tracking-[1px] px-2 py-1 rounded border border-paper-100/20 text-paper-100/70 hover:border-paper-100/40 hover:text-paper-100 disabled:opacity-40 shrink-0">
                  {busy === j.jobKey ? '…' : 'Ack'}
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </Panel>
  );
}

// @block 2.2 Sessions — agent workforce volume + cost + no-ship share.
function SessionsPanel() {
  const [data, error, loading] = usePanel<OpsSessionsPayload>(getOpsSessions);
  return (
    <Panel title="Agent sessions" subtitle="7d / 30d" error={error} loading={loading}>
      {data && (
        <>
          <div className="grid grid-cols-2 gap-4 mb-4">
            <Stat label="Sessions · 7d" value={num(data.counts.sessions7d)} />
            <Stat label="Est cost · 7d" value={usd(data.counts.cost7d)} />
            <Stat label="Sessions · 30d" value={num(data.counts.sessions30d)} />
            <Stat label="Est cost · 30d" value={usd(data.counts.cost30d)} />
          </div>
          <div className="pt-3 border-t border-paper-100/5 flex items-baseline justify-between text-xs">
            <span className="text-muted">No-ship share · 30d ({num(data.noship30d)}/{num(data.scored30d)} scored)</span>
            <span className="text-lg font-bold tabular-nums"
                  style={{ color: data.noshipShare != null && data.noshipShare > 0.5 ? 'var(--mt-stall, #f87171)' : 'var(--mt-watch, #fbbf24)' }}>
              {pct(data.noshipShare)}
            </span>
          </div>
        </>
      )}
    </Panel>
  );
}

// @block 2.3 Drift — unresolved dev-audit drift by severity + latest run.
function DriftPanel() {
  const [data, error, loading] = usePanel<OpsDriftPayload>(getOpsDrift);
  return (
    <Panel title="Code drift"
           subtitle={data?.latestRunId != null ? `run #${data.latestRunId} · ${ago(data.latestRunAt)}` : undefined}
           error={error} loading={loading}>
      {data && (
        <>
          <div className="mb-4"><Stat label="Unresolved findings" value={num(data.totalUnresolved)} /></div>
          <div className="space-y-2">
            {data.bySeverity.length === 0 && <div className="text-sm text-muted">No unresolved drift.</div>}
            {data.bySeverity.map((s) => (
              <div key={s.severity} className="flex items-center justify-between text-sm">
                <span className="flex items-center gap-2">
                  <span className="inline-block w-2 h-2 rounded-sm" style={{ background: SEVERITY_COLOR[s.severity] ?? '#6b7280' }} />
                  <span className="text-paper-100/80 capitalize">{s.severity}</span>
                </span>
                <span className="text-paper-100 font-bold tabular-nums">{num(s.count)}</span>
              </div>
            ))}
          </div>
        </>
      )}
    </Panel>
  );
}

// @block 2.4 GitHub — repo/branch/PR hygiene at the latest audit run.
function GithubPanel() {
  const [data, error, loading] = usePanel<OpsGithubPayload>(getOpsGithub);
  return (
    <Panel title="GitHub hygiene"
           subtitle={data?.latestRunId != null ? `run #${data.latestRunId} · ${ago(data.ingestedAt)}` : undefined}
           error={error} loading={loading}>
      {data && data.latestRunId != null ? (
        <div className="grid grid-cols-2 gap-4">
          <Stat label="Repos" value={num(data.repos)} />
          <Stat label="Open PRs > 30d" value={num(data.openPrsOver30d)}
                tone={(data.openPrsOver30d ?? 0) > 0 ? 'var(--mt-watch, #fbbf24)' : undefined} />
          <Stat label="Branches (non-default)" value={num(data.nonDefaultBranches)} />
          <Stat label="Unprotected defaults" value={num(data.unprotectedDefaultBranches)}
                tone={(data.unprotectedDefaultBranches ?? 0) > 0 ? 'var(--mt-watch, #fbbf24)' : undefined} />
        </div>
      ) : data ? (
        <div className="text-sm text-muted">No github audit run yet.</div>
      ) : null}
    </Panel>
  );
}

// @block 2.5 Deploys — last deploy per app + failures last 7d.
function DeploysPanel() {
  const [data, error, loading] = usePanel<OpsDeploysPayload>(getOpsDeploys);
  return (
    <Panel title="Deploys" subtitle={data ? `${data.failures7d} failed · 7d` : undefined}
           error={error} loading={loading}>
      {data && (
        <div className="space-y-1.5">
          {data.apps.map((a) => (
            <div key={a.appName} className="flex items-center gap-3 py-1.5 border-t border-paper-100/5 first:border-t-0">
              <span className="inline-block w-2.5 h-2.5 rounded-full shrink-0"
                    style={{ background: a.success ? 'var(--mt-ok, #4ade80)' : 'var(--mt-stall, #f87171)' }} />
              <div className="min-w-0 flex-1">
                <div className="text-sm text-paper-100/90 truncate">{a.appName}</div>
                <div className="text-[11px] text-muted truncate">
                  {a.success ? 'success' : 'failed'} · {ago(a.startedAt)}
                  {a.gitCommitShort ? ` · ${a.gitCommitShort}` : ''}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </Panel>
  );
}

// @block 2.6 Campaigns — headline delivered/replies off the latest snapshot.
function CampaignsPanel() {
  const [data, error, loading] = usePanel<OpsCampaignsPayload>(getOpsCampaigns);
  return (
    <Panel title="Campaigns"
           subtitle={data?.latestSnapshot ? `snapshot ${ago(data.latestSnapshot)}` : undefined}
           error={error} loading={loading}>
      {data && (
        <div className="grid grid-cols-2 gap-4">
          <Stat label="Campaigns" value={num(data.campaigns)} />
          <Stat label="Leads" value={num(data.leads)} />
          <Stat label="Delivered" value={num(data.delivered)} />
          <Stat label="Replies" value={num(data.replies)}
                tone={(data.replies ?? 0) > 0 ? 'var(--mt-ok, #4ade80)' : undefined} />
        </div>
      )}
    </Panel>
  );
}

// @section 3 Page component

export default function MissionControl() {
  return (
    <div className="md:-mx-10 md:-my-8">
      <section className="bg-ink-950 text-paper-100 min-h-screen font-sans">
        <div className="px-4 py-5 md:px-10 md:py-7 max-w-[1400px] mx-auto">
          {/* @block 3.1 Header */}
          <div className="flex items-center justify-between text-xs mb-6">
            <div className="text-muted">
              <span className="text-paper-100">Atlas</span>
              <span className="opacity-40"> / </span>
              <span className="text-paper-100/80">Mission Control</span>
            </div>
          </div>
          <div className="mb-8">
            <div className="text-[11px] uppercase tracking-[3px] text-muted mb-2">Six operational telemetry planes</div>
            <h1 className="text-3xl md:text-5xl font-bold tracking-tight text-paper-100">Vertis Mission Control</h1>
          </div>

          {/* @block 3.2 Panel grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
            <JobsPanel />
            <SessionsPanel />
            <DriftPanel />
            <GithubPanel />
            <DeploysPanel />
            <CampaignsPanel />
          </div>
        </div>
      </section>
    </div>
  );
}
